import { useCallback, useMemo } from "react";

import { useMutation, useQuery } from "@apollo/client";
import { FormikProvider, useFormik } from "formik";
import { capitalize, isEmpty, sortBy } from "lodash";
import { Form } from "react-bootstrap";
import { toast } from "react-toastify";

import difference from "components/calculations/utils/difference";
import ToggleButtons from "components/common/ToggleButtons";
import SelectField from "components/common/inputs/Select/SelectField";
import TaxYearSelect from "components/common/inputs/Select/TaxYearSelect";
import TextInput from "components/common/inputs/TextInput";

import useModal from "hooks/useModal";

import assignObjectValuesToSet from "utils/assignObjectValuesToSet";

import UPDATE_CLIENT from "@gql/mutations/clients/updateClient";
import CLIENT from "@gql/queries/client/client";
import ALL_COURTS from "@gql/sanity/allCourts";

const ClientDocumentSettingsForm = ({ formId }) => {
  const {
    options: {
      client,
      jurisdiction,
      courtId,
      taxYear,
      onJurisdictionChange,
      onTaxYearChange,
      onSuccess,
    } = {},
    close,
  } = useModal("CLIENT_DOCUMENT_SETTINGS");

  const [updateUser] = useMutation(UPDATE_CLIENT);

  const { data: courtsData, loading: courtsLoading } = useQuery(ALL_COURTS, {
    variables: { where: { province: { _id: { eq: jurisdiction } } } },
    skip: !jurisdiction,
  });
  const { data: clientData, loading } = useQuery(CLIENT, {
    variables: { where: { id: client?.id } },
    skip: !client?.id,
    fetchPolicy: "cache-first",
  });

  const courtLocations = useMemo(
    () =>
      sortBy(
        courtsData?.allCourt
          .find((court) => court._id === courtId)
          ?.locations?.map(({ name, _id }) => ({ value: _id, label: name, id: _id })),
      ),
    [courtsData?.allCourt, courtId],
  );

  const initialValues = useMemo(
    () => ({
      isApplicant: clientData?.client?.isApplicant,
      isPlaintiff: clientData?.client?.isPlaintiff,
      courtFile: clientData?.client?.court?.courtFile || "",
      courtLocationId:
        courtLocations?.find((c) => c.value === clientData?.client?.court?.courtLocationId)
          ?.value || "",
      fieldsSecurity: "LOCKED",
      taxYear: taxYear || new Date().getFullYear().toString(),
    }),
    [clientData?.client, courtLocations, taxYear],
  );

  const handleSubmitForm = useCallback(
    (values, { setSubmitting }) => {
      const differenceData = difference(values, initialValues);

      if (isEmpty(differenceData)) return null;

      setSubmitting(true);

      const { isApplicant, isPlaintiff, fieldsSecurity, ...upsert } = values;

      const variables = {
        data: assignObjectValuesToSet({
          court: {
            upsert: {
              create: { ...upsert, courtId },
              update: assignObjectValuesToSet({ ...upsert, courtId }),
            },
          },
          isPlaintiff,
          isApplicant,
        }),
        where: {
          id: client?.id,
        },
      };

      updateUser({ variables })
        .then(() => {
          toast.success("Settings has been successfully changed");
          close();
        })
        .catch((err) => err.graphQLErrors.map(({ message }) => toast.error(message)))
        .finally(() => {
          onSuccess?.(fieldsSecurity);
          setSubmitting(false);
        });
    },
    [updateUser, initialValues, client?.id, close, courtId, onSuccess],
  );

  const formik = useFormik({
    initialValues,
    validateOnBlur: false,
    enableReinitialize: true,
    onSubmit: (vals, helpers) => {
      onJurisdictionChange?.(jurisdiction);
      onTaxYearChange?.(vals.taxYear);
      handleSubmitForm(vals, helpers);
    },
  });

  const { handleSubmit, isSubmitting, setFieldValue, values } = formik;

  return (
    <FormikProvider value={formik}>
      <Form id={formId} onSubmit={handleSubmit} className="create-document-form">
        <SelectField
          name="courtLocationId"
          options={courtLocations}
          containerClassName="mb-3"
          label="Court Location"
          isLoading={courtsLoading}
          disabled={isSubmitting || loading}
          placeholder="Select court location"
        />
        <TextInput
          name="courtFile"
          label="Court File"
          placeholder="Court File"
          containerClassName="mb-4"
          disabled={isSubmitting || loading}
        />
        <SelectField
          name="jurisdiction"
          options={[]}
          containerClassName="mb-3"
          label="Jurisdiction"
          placeholder={jurisdiction || "Jurisdiction"}
          disabled
        />
        <TaxYearSelect
          label="Tax Year"
          value={values.taxYear}
          onChange={({ value }) => {
            setFieldValue("taxYear", value);
          }}
        />
        <ToggleButtons
          label={`Is ${capitalize(clientData?.client?.clientLabel)} Plaintiff?`}
          name="isPlaintiff"
          buttons={[
            {
              value: true,
              label: "Plaintiff",
            },
            {
              value: false,
              label: "Defendant",
            },
          ]}
          disabled={isSubmitting || loading}
          className="mt-3"
        />
        <ToggleButtons
          label={`Is ${capitalize(clientData?.client?.clientLabel)} Applicant?`}
          name="isApplicant"
          buttons={[
            {
              value: true,
              label: "Applicant",
            },
            {
              value: false,
              label: "Respondent",
            },
          ]}
          disabled={isSubmitting || loading}
        />
        <ToggleButtons
          label="Field protection"
          name="fieldsSecurity"
          hint="Disabling protection may cause issues when editing the document in the Word Add-In"
          buttons={[
            {
              value: "LOCKED",
              label: "Locked",
            },
            {
              value: "UNLOCKED",
              label: "Unlocked",
            },
          ]}
          disabled={isSubmitting || loading}
          className="mb-0"
        />
      </Form>
    </FormikProvider>
  );
};

export default ClientDocumentSettingsForm;
